/**
 * The shift template palette.
 *
 * Templates are the fast path: a boss builds most of a week by dragging four or
 * five known shapes onto the grid. Each chip is a draggable that carries its
 * times, so a drop needs no round trip to find out what it is creating and the
 * optimistic card can render immediately.
 *
 * Clicking a chip is not a no-op either: it selects the template, and a click on
 * an empty cell then places it. That is the keyboard-and-trackpad path, and it
 * is what makes the grid usable on a laptop with no mouse.
 */
import { useEffect, useRef, useState } from 'react';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { GripVertical, Plus } from 'lucide-react';
import type { ShiftTemplate } from '../lib/api.ts';
import { fmtDuration, fmtTimeRange, shiftMinutes } from '../lib/time.ts';
import { colourTokens } from '../components/Bits.tsx';
import { TEMPLATE_DRAG, type TemplateDrag } from './dnd.ts';
import { drawDragPreview } from './dragPreview.ts';

function TemplateChip({
  template,
  selected,
  onSelect,
}: {
  template: ShiftTemplate;
  selected: boolean;
  onSelect: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [dragging, setDragging] = useState(false);
  const tokens = colourTokens(template.colour);
  const minutes = shiftMinutes(template.startTime, template.endTime);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const payload: TemplateDrag = {
      type: TEMPLATE_DRAG,
      templateId: template.id,
      name: template.name,
      startTime: template.startTime,
      endTime: template.endTime,
      colour: template.colour,
    };
    return draggable({
      element: el,
      getInitialData: () => ({ ...payload }),
      onGenerateDragPreview: ({ nativeSetDragImage }) =>
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: pointerOutsideOfPreview({ x: '12px', y: '10px' }),
          render: ({ container }) => {
            drawDragPreview(container, {
              title: fmtTimeRange(template.startTime, template.endTime),
              subtitle: template.name,
              colour: tokens,
            });
          },
        }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    });
  }, [template.id, template.name, template.startTime, template.endTime, template.colour, tokens.soft, tokens.solid]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-tip={`${template.name}|${fmtTimeRange(template.startTime, template.endTime)}, ${fmtDuration(
        minutes,
      )}. Drag it onto the grid, or select it and click an empty cell.`}
      className={`flex shrink-0 cursor-grab items-center gap-2 px-2.5 py-1.5 active:cursor-grabbing ${
        dragging ? 'is-dragging' : ''
      }`}
      style={{
        borderRadius: 'var(--sp-radius-sm)',
        background: tokens.soft,
        border: `1px solid ${selected ? tokens.solid : 'var(--sp-border)'}`,
        boxShadow: selected ? `0 0 0 2px ${tokens.solid}` : 'var(--sp-shadow-1)',
      }}
    >
      <GripVertical size={13} aria-hidden="true" style={{ color: tokens.solid }} />
      <span className="text-left">
        <span className="block text-[12.5px] font-bold leading-tight text-ink">{template.name}</span>
        <span className="tabular block text-[11px] leading-tight text-muted">
          {fmtTimeRange(template.startTime, template.endTime)}
        </span>
      </span>
    </button>
  );
}

export function TemplatePalette({
  templates,
  selectedId,
  onSelect,
  onManage,
}: {
  templates: ShiftTemplate[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onManage: () => void;
}) {
  const active = templates.filter((t) => t.active !== false);

  return (
    <div
      className="flex items-center gap-2 overflow-x-auto px-4 py-2.5"
      style={{ background: 'var(--sp-surface-2)', borderBottom: '1px solid var(--sp-border)' }}
    >
      <span className="shrink-0 pr-1 text-[11px] font-bold uppercase tracking-wide text-faint">Your usual shifts</span>
      {active.length === 0 ? (
        <span className="text-[12.5px] text-muted">
          No usual shifts set up yet. Add the ones you run every week in Settings, then drop them onto the roster.
        </span>
      ) : (
        active.map((t) => (
          <TemplateChip
            key={t.id}
            template={t}
            selected={selectedId === t.id}
            onSelect={() => onSelect(selectedId === t.id ? null : t.id)}
          />
        ))
      )}
      {active.length > 0 ? (
        <span className="shrink-0 pl-1 text-[12px] leading-snug text-muted">
          Drag one onto the roster, or click it then click a slot.
        </span>
      ) : null}
      <span className="flex-1" />
      <button
        type="button"
        className="btn btn-icon btn-sm shrink-0"
        onClick={onManage}
        title="Manage templates|Opens Settings, where you can add, rename and recolour the shapes you roster most often."
        aria-label="Manage shift templates"
      >
        <Plus size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
